import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaHourglassHalf, FaTimesCircle } from 'react-icons/fa';
import Button from '../../components/common/Button';
import { useAccountData } from '../../context/AccountDataContext';
import { useAuth } from '../../context/AuthContext';
import { clearTransactionCache } from '../../services/paymentService';
import { popPendingCheckoutUuid, resolveBalanceRands } from '../../utils/paymentCheckout';

const OUTCOMES = {
  success: {
    title: 'Payment Successful!',
    message: 'Your payment has been received. Thank you for your purchase.',
    titleClass: 'text-green-600',
    icon: <FaCheckCircle className="text-green-500 text-5xl" />,
  },
  pending: {
    title: 'Payment Processing',
    message: 'Your payment is processing — your balance will update shortly.',
    titleClass: 'text-amber-600',
    icon: <FaHourglassHalf className="text-amber-500 text-5xl" />,
  },
  failed: {
    title: 'Payment Not Completed',
    message: "Your payment didn't go through and nothing was charged for this attempt.",
    titleClass: 'text-red-600',
    icon: <FaTimesCircle className="text-red-500 text-5xl" />,
  },
};

/**
 * Presentational card for a payment outcome. Hook-free so tests can assert it
 * with renderToStaticMarkup in the node-environment Vitest setup.
 */
export const PaymentResultCard = ({
  outcome,
  balanceRands,
  checkoutUuid,
  countdown,
  onGoToDashboard,
  onTryAgain,
}) => {
  const copy = OUTCOMES[outcome];

  return (
    <div className="min-h-screen flex items-start justify-center bg-hooray-beige-40 pt-16 px-4">
      <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full text-center">
        <div className="flex justify-center mb-3">{copy.icon}</div>
        <h1 className={`text-2xl font-bold mb-3 font-mont ${copy.titleClass}`}>{copy.title}</h1>
        <p className="text-hooray-dark-text mb-5">{copy.message}</p>

        {outcome === 'success' && typeof balanceRands === 'number' && (
          <div className="bg-gray-50 p-4 rounded-md mb-3">
            <p className="text-sm text-hooray-dark-text">
              Current balance: <span className="font-medium">R{balanceRands.toFixed(2)}</span>
            </p>
          </div>
        )}

        {checkoutUuid && (
          <p className="text-xs text-gray-400 mb-3">Reference: {checkoutUuid}</p>
        )}

        {outcome === 'failed' && (
          <Button variant="cta" onClick={onTryAgain} className="mb-2">
            Try Again
          </Button>
        )}

        {typeof countdown === 'number' && (
          <div className="mt-5 border-t pt-4 text-sm text-gray-500">
            Redirecting to dashboard in {countdown} seconds...
          </div>
        )}

        <Button
          variant={outcome === 'failed' ? 'secondary' : 'cta'}
          onClick={onGoToDashboard}
          className="mt-3"
        >
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
};

/**
 * Landing page for the hosted checkout return redirect. The redirect is only
 * a UX hint; the webhook-credited account balance is the real confirmation,
 * so this page refreshes it rather than trusting the route alone.
 */
const PaymentResultPage = ({ outcome }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { accountBalance, fetchAccountBalance } = useAccountData();
  const [checkoutUuid, setCheckoutUuid] = useState(null);
  const [countdown, setCountdown] = useState(outcome === 'success' ? 5 : null);

  useEffect(() => {
    setCheckoutUuid(popPendingCheckoutUuid());

    if (user?.accountUuid) {
      clearTransactionCache(user.accountUuid);
    }

    if (outcome === 'success') {
      fetchAccountBalance();
    }
    // Run once on mount: this effect settles the stashed checkout attempt.
    // If contexts haven't hydrated accountUuid yet, fetchAccountBalance
    // no-ops - AccountDataContext's init effect fetches again once it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof countdown !== 'number') return undefined;
    if (countdown <= 0) {
      navigate('/dashboard');
      return undefined;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  return (
    <PaymentResultCard
      outcome={outcome}
      balanceRands={resolveBalanceRands(accountBalance)}
      checkoutUuid={checkoutUuid}
      countdown={countdown}
      onGoToDashboard={() => navigate('/dashboard')}
      onTryAgain={() => navigate('/billing')}
    />
  );
};

export default PaymentResultPage;
